import {
  ArrowRightIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import sampleLicense from '~/assets/landing/sample-license.webp';
import { CheckList } from './LandingElements';

const extractionBenefits = [
  'AI-powered information extraction',
  'Review and confirm extracted information',
  'Structured, standardized credential data',
  'Reduce repetitive manual data entry',
  'Keep your professional profile current',
];

const LicenseData = () => (
  <div className="landing-license-data">
    <h4>Extracted Information</h4>
    <dl>
      {[
        ['License Type', 'Medical License'],
        ['Number', 'AB.123456'],
        ['Location', 'Ohio'],
        ['Issue Date', '01/15/2026'],
        ['Expiration Date', '01/14/2028'],
        ['Status', 'Active'],
      ].map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const ExtractionPreview = () => (
  <div
    className="landing-extraction-preview"
    role="img"
    aria-label="Example: AI extracts license type, number, location, dates, and status from an uploaded medical license."
  >
    <img src={sampleLicense} alt="" width="100" height="150" loading="lazy" />
    <ArrowRightIcon className="landing-flow-arrow" aria-hidden="true" />
    <LicenseData />
  </div>
);

const UploadPreview = () => (
  <div className="landing-upload-preview" role="img" aria-label="Example document upload area">
    <ArrowUpTrayIcon aria-hidden="true" />
    <strong>Upload and process with AI</strong>
    <p>Drop your professional documents here</p>
    <DocumentTextIcon aria-hidden="true" />
    <span>Your documents. Your control.</span>
  </div>
);

const ProfilePreview = () => (
  <div
    className="landing-profile-preview"
    role="img"
    aria-label="Example professional profile with 34 total records: 33 active, 1 expiring soon, and 0 expired."
  >
    <div className="landing-profile-identity">
      <UserCircleIcon aria-hidden="true" />
      <strong>Jane Smith, MD</strong>
      <span>Doctor of Medicine</span>
    </div>
    <div className="landing-profile-stats">
      <span>Quick Stats</span>
      <div>
        {[
          ['34', 'Total Records'],
          ['33', 'Active'],
          ['1', 'Expiring Soon'],
          ['0', 'Expired'],
        ].map(([value, label]) => (
          <div key={label}>
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const McvSections = () => (
  <>
    <section className="landing-extraction" aria-labelledby="landing-extraction-title">
      <div className="landing-container">
        <h2 id="landing-extraction-title">From Documents to an AI-Powered Professional Profile</h2>
        <div className="landing-extraction-layout">
          <div>
            <h3>AI-Powered Credential Extraction</h3>
            <p>
              Upload your professional documents and let AI extract, organize, and populate your
              credential information for review.
            </p>
            <CheckList items={extractionBenefits} />
          </div>
          <ExtractionPreview />
        </div>
      </div>
    </section>
    <section className="landing-process landing-container" aria-label="How MedCredVault works">
      <ol>
        <li>
          <div className="landing-step-heading">
            <span>1</span>
            <h3>Upload Your Documents</h3>
          </div>
          <p>
            Upload licenses, certifications, education, employment records, insurance documents, and
            more.
          </p>
          <UploadPreview />
        </li>
        <li>
          <div className="landing-step-heading">
            <span>2</span>
            <h3>AI Extracts and Organizes</h3>
          </div>
          <p>
            AI automatically extracts key information, structures your credential data, and
            populates your professional profile.
          </p>
          <ExtractionPreview />
        </li>
        <li>
          <div className="landing-step-heading">
            <span>3</span>
            <h3>Review, Confirm, and Update</h3>
          </div>
          <p>
            Review AI-extracted information, make any necessary corrections, and keep your profile
            current.
          </p>
          <ProfilePreview />
        </li>
      </ol>
    </section>
  </>
);
